<template>
  <div class="crypto-currency-wallets-select">
    <div class="crypto-currency-wallets-select__header">
      <ExchangeDropDownHeader @close="emit('close')" />
    </div>
    <div class="crypto-currency-wallets-select__tabs">
      <ExchangeWalletTabs
        v-if="!props.loading"
        v-model="walletType"
      />
    </div>

    <div
      v-if="props.loading"
      class="crypto-currency-wallets-select__loading"
    >
      <ExchangeLoading />
    </div>

    <template v-else>

      <div v-if="walletType === WalletTypes.EXTERNAL" class="crypto-currency-wallets-select__search">
        <FilterInput v-model="searchCryptoCurrency" />
      </div>

      <template v-if="walletType === WalletTypes.PAYBIS">
        <ExchangeWalletMemberLayout
          v-if="TokenService.hasToken()"
          :flow="props.flow"
          :relationLink="relationLink"
        />
        <ExchangeWalletGuestLayout v-else />
      </template>
      <template v-if="walletType === WalletTypes.EXTERNAL">
        <ExchangeDropDownEmptyFilter v-if="isShowEmptyResult" />
        <PbScroll v-else>
          <div class="crypto-currency-wallets-select__list">
            <CryptoCurrency
              class="crypto-currency-item-override"
              v-for="(option, index) in items"
              :key="index"
              :selected="props.value instanceof CryptoOptionModel && props.value.currency === option.currency"
              :currency="option.currency"
              :icon="option.icon"
              :label="option.name"
              @click.native="selectHandler(option)"
            />
          </div>
        </PbScroll>
      </template>
    </template>
  </div>
</template>

<script lang="ts" setup>
import TokenService from '@paybis/frontend-common-lib/src/services/token';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

import CryptoCurrency from '@kano/components/ExchangeFormV2/CryptoCurrency.vue';
import ExchangeLoading from '@/components/ExchangeFormV2/ExchangeLoading.vue';
import ExchangeWalletGuestLayout from '@kano/components/ExchangeFormV2/ExchangeWalletGuestLayout.vue';
import ExchangeWalletMemberLayout from '@kano/components/ExchangeFormV2/ExchangeWalletMemberLayout.vue';
import ExchangeWalletTabs from '@kano/components/ExchangeFormV2/ExchangeWalletTabs.vue';
import FilterInput from '@kano/components/ExchangeFormV2/FilterInput.vue';
import PbScroll from '@kano/components/ExchangeFormV2/PbScroll.vue';
import { BaseOptionModel, CryptoOptionModel } from '@/models';
import { WalletTypes } from '@/types';
import { i18n } from '@kano/i18n';

import ExchangeDropDownHeader from './ExchangeDropDownHeader.vue';
import ExchangeDropDownEmptyFilter from './ExchangeDropDownEmptyFilter.vue';
import { DataLayerEvent, pushToDataLayer } from '@paybis/frontend-common-lib/src/services/gtm';
import { Flow } from '@paybis/frontend-common-lib/src/types/exchangeForm/exchange-form';

const props = defineProps<{
  value: BaseOptionModel
  flow: Flow
  items: CryptoOptionModel[]
  loading: boolean
  relationLink: string,
}>();

const emit = defineEmits<{
  (event: 'close'): void,
  (event: 'input', option: CryptoOptionModel): void,
}>();

const searchCryptoCurrency = ref('');
onUnmounted(() => searchCryptoCurrency.value = '');

const items = computed(() => {
  if (searchCryptoCurrency.value.length) {
    const search = searchCryptoCurrency.value.toLowerCase();
    return props.items.filter(item => {
      return item.currency.toLowerCase().includes(search) 
      || item.name.toLowerCase().includes(search)
      || (i18n.t(`asset-names.${item.currency}`) as string).toLocaleLowerCase().includes(search);
    });
  }

  return props.items;
});

const walletType = ref<WalletTypes>(WalletTypes.EXTERNAL);

watch(walletType, () => searchCryptoCurrency.value = '');

const selectHandler = (option: CryptoOptionModel) => {
  emit('input', option);
  pushToDataLayer({ event: DataLayerEvent.FROM_TICKER_CHANGE });
  emit('close');
};

const isShowEmptyResult = computed(() => {
  return walletType.value === WalletTypes.EXTERNAL &&
    searchCryptoCurrency.value &&
    items.value.length === 0;
});

onMounted(() => {
  pushToDataLayer({ event: DataLayerEvent.FROM_TICKER_VIEW });
});
</script>

<style lang="scss" scoped>
@use 'scss-vars';

@use '@kanoAssets/styles/breakpoints.scss';

.crypto-currency-wallets-select {
  display: flex;
  flex-direction: column;
  height: 100%;

  @include breakpoints.desktop {
    height: 512px;
  }

  &__header {
    @include breakpoints.desktop {
      display: none;
    }
  }

  &__sub-header {
    padding: 6px 16px;

    @media screen and (max-width: scss-vars.$screen-lg-min) {
      padding: 8px 24px;
    }
  }

  &__close {
    display: none;
    width: 24px;
    height: 24px;

    & svg {
      width: 24px;
      height: 24px;
    }

    @media screen and (max-width: scss-vars.$screen-lg-min) {
      display: block;
    }
  }

  &__list {
    margin: 0 14px;
    height: 100%;
  }

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.crypto-currency-item-override {
  padding: 8px 5px;
}
</style>
